import { useStore } from '@nanostores/react';
import { memo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { Button } from '~/components/ui/Button';
import { removeSite } from '~/lib/.client/api/sites';
import { importStore } from '~/lib/projects';
import { descriptionStore } from '~/lib/stores/description';
import { classNames } from '~/utils/classNames';
import { withSpinner } from '~/utils/withSpinner';

interface UnpublishProjectProps {
  projectId: string;
  url: string;
  className?: string;
}

export const UnpublishProject = memo(({ projectId, url, className }: UnpublishProjectProps) => {
  const [confirmUnpublishProject, setConfirmUnpublishProject] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const description = useStore(descriptionStore);

  const handleCloseConfirmation = () => {
    setConfirmUnpublishProject(false);
  };

  const handleOpenConfirmation = () => {
    setConfirmUnpublishProject(true);
  };

  const handleUnpublishProject = useCallback(() => {
    withSpinner({
      action: async () => {
        try {
          await removeSite(projectId);
          importStore.setKey('deploymentInfo', undefined);
        } catch (error: unknown) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      },
      onStateUpdate: setIsLoading,
    });
  }, []);

  return (
    <>
      <div className={classNames('flex justify-between gap-2', className)}>
        <div className="flex flex-col gap-1 text-sm">
          <h3 className="font-medium">Unpublish Project</h3>
          <p className="text-bolt-elements-textSecondary max-w-[85%]">
            Take your project offline from its live domain. It will no longer be available at its public URL until you
            publish it again.
          </p>
        </div>
        <Button variant="link-color" color="danger" onClick={handleOpenConfirmation}>
          Unpublish
        </Button>
      </div>

      <ConfirmationDialog
        titleText="Unpublish this project?"
        headerWrapperClassName="gap-6"
        maxWidth={512}
        confirmButtonNode="Unpublish"
        confirmButtonVariant="danger"
        confirmButtonType="tint"
        open={confirmUnpublishProject}
        isConfirming={isLoading}
        onClose={handleCloseConfirmation}
        handleConfirm={handleUnpublishProject}
      >
        <p>
          Are you sure you want to unpublish{' '}
          <strong className="font-semibold text-bolt-ds-textPrimary">"{description}"</strong>?
        </p>
        <p className="mt-2">
          Your project will be taken offline and will no longer be accessible at{' '}
          <b className="font-semibold text-bolt-ds-textPrimary">{url}</b>
        </p>
      </ConfirmationDialog>
    </>
  );
});

UnpublishProject.displayName = 'UnpublishProject';
